import { Fragment, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link, useHistory, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { Button } from '@lego/klik-react';
import { Step, Stepper } from './stepper';
import { OverlayPortal } from './OverlayPortal';
import { Spinner } from './spinner/Spinner';
import { spacing, colors } from '../config';
import { useLabels } from '../providers/LabelsProvider';
import { useUser } from '../providers/UserProvider';
import {
  adjustAssessmentsBasedOnFeatureFlags,
  enhanceQuestionsWithAnswersAndProof,
  isAnyQuestionMissingProof,
} from './survey/helpers';
import { GET_SURVEY_PAGE, FINISH_SURVEY } from '../queries/survey';
import { QuestionReview } from './question/QuestionReview';
import { routes } from './RouteHandler';
import { Heading } from './Heading';
import { useFetchImagesFromS3 } from '../hooks/useFetchImagesFromS3';
import { generalApiErrorHandler } from '../utils/generalApiErrorHandler';
import { throwErrorWithTransactionName } from '../utils/elasticApmHeler';
import { withTransaction } from '@elastic/apm-rum-react';
import { MissingProofWarningModal } from './question/modals/MissingProofWarningModal';
import { MissingProofExplainer } from './MissingProofExplainer';

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
});

const missingProofWrapperStyle = css({
  marginTop: spacing.size8,
});

const buttonWrapperStyle = css({
  width: 'auto',
  display: 'flex',
  justifyContent: 'center',
  marginTop: spacing.size12,
});

const firstButtonStyle = css({
  marginRight: spacing.size8,
});

const Review = () => {
  const history = useHistory();
  const { staticUserVariables } = useUser();

  const { year, version } = useParams();
  const [isProofWarningModalVisible, setIsProofWarningModalVisible] =
    useState(false);

  const {
    labels: {
      svy_review_headline,
      svy_end_edit,
      svy_end_finish,
      svy_back_to_homepage,
      svy_snackbar_error_header,
      svy_snackbar_error_default_text,
    },
  } = useLabels();

  const {
    data: reviewData,
    loading: isReviewPageLoading,
    error: reviewDataError,
  } = useQuery(GET_SURVEY_PAGE, {
    variables: staticUserVariables({ year, version }),
    fetchPolicy: 'network-only',
  });

  const [finishSurvey, { loading: finishSurveyLoading }] = useMutation(
    FINISH_SURVEY,
    {
      onCompleted: handleFinishSurveyResponse,
    }
  );

  const { uploadedImagesKeys } = useFetchImagesFromS3({
    uploadId: reviewData?.getSurveyInfo?.uploadId,
    skip: !reviewData,
  });

  async function submitSurvey() {
    try {
      await finishSurvey({
        variables: staticUserVariables({ year, version }),
      });
    } catch (error) {
      generalApiErrorHandler({
        error,
        snackbarHeader: svy_snackbar_error_header,
        snackbarMessage: svy_snackbar_error_default_text,
      });
    }
  }

  async function handleFinishSurvey() {
    if (isAnyQuestionMissingProof(enhancedQuestions)) {
      setIsProofWarningModalVisible(true);
      return;
    }

    await submitSurvey();
  }

  function handleFinishSurveyResponse(response) {
    if (response.finishSurvey) history.push(`/${routes.confirmation}`);
  }

  // Error boundary catches this error
  if (reviewDataError)
    throwErrorWithTransactionName(reviewDataError, 'ReviewPage');

  if (isReviewPageLoading || finishSurveyLoading) {
    return (
      <OverlayPortal>
        <Spinner />
      </OverlayPortal>
    );
  }

  const {
    getAnswers: savedAnswers,
    getSurvey: questions,
    getSurveyInfo: { surveyState },
  } = reviewData;

  const surveyDetailsForFeatureFlag = {
    year: Number(year),
  };

  let enhancedQuestions = enhanceQuestionsWithAnswersAndProof(
    questions,
    savedAnswers,
    uploadedImagesKeys
  );
  enhancedQuestions = adjustAssessmentsBasedOnFeatureFlags(
    enhancedQuestions,
    surveyDetailsForFeatureFlag
  );

  return (
    <div css={containerStyle}>
      {isProofWarningModalVisible && (
        <MissingProofWarningModal
          onClose={() => {
            setIsProofWarningModalVisible(false);
          }}
          onConfirm={() => {
            setIsProofWarningModalVisible(false);
            history.push(`/${routes.survey}/${year}/${version}?mode=edit`);
          }}
        />
      )}
      <Heading type="h1" text={svy_review_headline} />
      <Stepper activeColor={colors.boldColors.lightBlue.lightBlue100}>
        {enhancedQuestions.map((question) => {
          const {
            questionId,
            headline,
            savedAnswer,
            questionImageKeys,
            proofRequirement,
          } = question;
          return (
            <Step
              stepId={questionId}
              key={questionId}
              header={headline}
              isValid={savedAnswer.isValid}
              isWarning={proofRequirement.isProofMissing}
              isComplete={savedAnswer.isComplete}
            >
              <QuestionReview
                question={question}
                answer={savedAnswer}
                uploadImageKeys={questionImageKeys}
              />
            </Step>
          );
        })}
      </Stepper>
      <div css={missingProofWrapperStyle}>
        {isAnyQuestionMissingProof(enhancedQuestions) && (
          <MissingProofExplainer />
        )}
      </div>
      <div css={buttonWrapperStyle}>
        {surveyState.isEditable ? (
          <Fragment>
            <div css={firstButtonStyle}>
              <Link
                to={{
                  pathname: `/${routes.survey}/${year}/${version}`,
                  search: '?mode=edit',
                }}
              >
                <Button
                  data-transaction-name="Edit Survey"
                  label={svy_end_edit}
                  variant="outline"
                  css={firstButtonStyle}
                />
              </Link>
            </div>
            <Button
              data-transaction-name="Finish Survey"
              label={svy_end_finish}
              onClick={handleFinishSurvey}
            />
          </Fragment>
        ) : (
          <Link to={`/${routes.homepage}`}>
            <Button
              data-transaction-name="Back to Homepage"
              label={svy_back_to_homepage}
            />
          </Link>
        )}
      </div>
    </div>
  );
};

const ReviewPage = withTransaction('ReviewPage', 'component')(Review);
export { ReviewPage };
